import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Button,
  Card,
  SimpleGrid,
  Spinner,
  Icon,
  Input,
  Table,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiUpload, FiFile, FiDownload, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { toaster } from '../components/ui/toaster';
import Navbar from '../components/Navbar';
import { useReview, useDownloadReviewReport } from '../hooks/useApi';

interface FieldCheckItem {
  field: string;
  label?: string;
  found: boolean;
  note?: string;
}

interface FieldCheckEntry {
  type_key: string;
  checks: FieldCheckItem[];
}

interface Evidence {
  id: string | number;
  filename: string;
  file_size: number;
  description?: string;
  evidence_types?: string[];
  field_checks?: FieldCheckEntry[];
  verification_status?: 'accepted' | 'rejected';
  verification_comments?: string;
  skipped_by_user?: boolean;
  auto_detected_sections?: Record<string, { confidence?: number }>;
  confidence_score?: number;
}

function authHeaders(json = false): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

const ReviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: review, isLoading, refetch } = useReview(id || '');
  const downloadMutation = useDownloadReviewReport();

  const [evidenceFiles, setEvidenceFiles] = useState<Evidence[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});
  const [isReassessing, setIsReassessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);

  const isCompleted = review?.status === 'completed' || review?.status === 'completed_with_errors';

  const loadEvidence = useCallback(async () => {
    if (!id) return;
    setLoadingEvidence(true);
    try {
      const res = await fetch(`/api/evidence/${id}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        setEvidenceFiles(await res.json());
      }
    } finally {
      setLoadingEvidence(false);
    }
  }, [id]);

  useEffect(() => {
    if (isCompleted) loadEvidence();
  }, [isCompleted, loadEvidence]);

  const mapEndpoint = useCallback(async (evidenceId: string | number, action: 'map' | 'skip') => {
    const res = await fetch(`/api/evidence/${evidenceId}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(true),
      body: JSON.stringify({
        system_standard_id: review?.system_standard_id,
        selected_sections: review?.results?.selected_sections || [],
        selected_subsections: {},
        language: review?.language || 'en',
      }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || t('createReview.uploadFailed'));
    return res.json();
  }, [review, t]);

  const uploadFile = useCallback(async (file: File) => {
    if (!id) return;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`/api/evidence/upload/${id}`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || t('createReview.uploadFailed'));
    }
    return res.json();
  }, [id, t]);

  const handleFilesSelect = useCallback(async (files: File[]) => {
    if (!files.length) return;
    setIsUploading(true);
    try {
      for (const file of files) {
        const uploaded = await uploadFile(file);
        if (uploaded && uploaded.verification_status !== 'rejected') {
          await mapEndpoint(uploaded.id, 'map');
        }
      }
      toaster.create({ title: t('createReview.evidenceUploadedSuccessfully'), type: 'success' });
      await loadEvidence();
      await refetch();
    } catch (error) {
      toaster.create({
        title: t('createReview.uploadFailed'),
        description: error instanceof Error ? error.message : undefined,
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  }, [uploadFile, mapEndpoint, loadEvidence, refetch, t]);

  const handleAcceptAnyway = useCallback(async (evidenceId: string | number) => {
    setBusyIds((p) => ({ ...p, [evidenceId]: true }));
    try {
      await mapEndpoint(evidenceId, 'map');
      await loadEvidence();
      await refetch();
    } catch (error) {
      toaster.create({ title: t('createReview.uploadFailed'), type: 'error' });
    } finally {
      setBusyIds((p) => ({ ...p, [evidenceId]: false }));
    }
  }, [mapEndpoint, loadEvidence, refetch, t]);

  const handleSkip = useCallback(async (evidenceId: string | number) => {
    setBusyIds((p) => ({ ...p, [evidenceId]: true }));
    try {
      await mapEndpoint(evidenceId, 'skip');
      await loadEvidence();
      await refetch();
    } catch (error) {
      toaster.create({ title: t('createReview.uploadFailed'), type: 'error' });
    } finally {
      setBusyIds((p) => ({ ...p, [evidenceId]: false }));
    }
  }, [mapEndpoint, loadEvidence, refetch, t]);

  const handleReplaceClick = useCallback((evidenceId: string | number) => {
    setReplacingId(String(evidenceId));
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const oldId = replacingId;
    e.target.value = '';
    setReplacingId(null);
    if (!file || !oldId) return;
    setBusyIds((p) => ({ ...p, [oldId]: true }));
    try {
      await mapEndpoint(oldId, 'skip');
      const uploaded = await uploadFile(file);
      if (uploaded && uploaded.verification_status !== 'rejected') {
        await mapEndpoint(uploaded.id, 'map');
      }
      await loadEvidence();
      await refetch();
    } catch (error) {
      toaster.create({ title: t('createReview.uploadFailed'), type: 'error' });
    } finally {
      setBusyIds((p) => ({ ...p, [oldId]: false }));
    }
  }, [replacingId, mapEndpoint, uploadFile, loadEvidence, refetch, t]);

  const handleReassess = useCallback(async () => {
    if (!id) return;
    setIsReassessing(true);
    try {
      const res = await fetch(`/api/reviews/${id}/reassess`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('createReview.uploadFailed'));
      }
      toaster.create({ title: t('reviewDetail.reassessStarted'), type: 'info' });
      // Poll status until it reaches a terminal state again.
      const poll = setInterval(async () => {
        const statusRes = await fetch(`/api/reviews/${id}/status`, {
          credentials: 'include',
          headers: authHeaders(),
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' || statusData.status === 'completed_with_errors' || statusData.status === 'failed') {
            clearInterval(poll);
            setIsReassessing(false);
            await refetch();
            await loadEvidence();
            toaster.create({ title: t('reviewDetail.reassessComplete'), type: 'success' });
          }
        }
      }, 3000);
    } catch (error) {
      setIsReassessing(false);
      toaster.create({
        title: t('reviewDetail.reassessFailed'),
        description: error instanceof Error ? error.message : undefined,
        type: 'error',
      });
    }
  }, [id, refetch, loadEvidence, t]);

  const handleDownload = useCallback(async () => {
    if (!id) return;
    try {
      const blob = await downloadMutation.mutateAsync(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NEUREV_Assessment_${id}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toaster.create({ title: t('reviews.downloadFailed'), type: 'error' });
    }
  }, [id, downloadMutation, t]);

  if (isLoading) {
    return (
      <>
        <Navbar />
        <Container maxW="4xl" py={10}>
          <Spinner />
        </Container>
      </>
    );
  }

  if (!review) {
    return (
      <>
        <Navbar />
        <Container maxW="4xl" py={10}>
          <Text>{t('reviewDetail.notFound')}</Text>
        </Container>
      </>
    );
  }

  const sectionResults: any[] = review.results?.section_results || [];
  const pendingSections: string[] = (review as any).pending_reassessment_sections || [];

  return (
    <>
      <Navbar />
      <Container maxW="6xl" py={8}>
        <VStack align="stretch" gap={6}>
          <HStack justify="space-between" wrap="wrap">
            <Button variant="ghost" size="sm" {...{ as: RouterLink, to: '/reviews' } as any}>
              <Icon as={FiArrowLeft} mr={2} />
              {t('reviewDetail.backToReviews')}
            </Button>
            {review.report_generated && (
              <Button size="sm" onClick={handleDownload} loading={downloadMutation.isLoading}>
                <Icon as={FiDownload} mr={2} />
                {t('reviews.downloadReport')}
              </Button>
            )}
          </HStack>

          <Card.Root variant="outline">
            <Card.Body>
              <VStack align="stretch" gap={2}>
                <HStack justify="space-between" wrap="wrap">
                  <Heading size="md">{review.name || t('reviewDetail.untitledAssessment')}</Heading>
                  <Badge colorPalette={review.status === 'completed' ? 'green' : review.status === 'completed_with_errors' ? 'orange' : 'gray'}>
                    {review.status}
                  </Badge>
                </HStack>
                <Text color="gray.600" fontSize="sm">
                  {t('reviews.standard')}: {review.system_standard?.name || t('reviews.unknownStandard')}
                </Text>
                {typeof review.compliance_score === 'number' && (
                  <Text fontSize="sm">
                    {t('reviewDetail.complianceScore')}: <b>{review.compliance_score}%</b>
                  </Text>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {!isCompleted && (
            <Card.Root variant="outline">
              <Card.Body>
                <Text>{t('reviewDetail.notYetCompleted', { status: review.status })}</Text>
              </Card.Body>
            </Card.Root>
          )}

          {isCompleted && (
            <>
              {sectionResults.length > 0 && (
                <Card.Root variant="outline">
                  <Card.Header>
                    <Heading size="sm">{t('reviewDetail.measureResults')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>{t('reviewDetail.measure')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('reviewDetail.rating')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('reviewDetail.score')}</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {sectionResults.map((r: any, idx: number) => {
                          const sec = r.section;
                          const sid = typeof sec === 'object' ? (sec?.section_id || sec?.section) : sec;
                          const isPending = pendingSections.includes(String(sid).toLowerCase());
                          return (
                            <Table.Row key={idx} bg={isPending ? 'yellow.50' : undefined}>
                              <Table.Cell>{sid}{isPending ? ` (${t('reviewDetail.pendingReassessment')})` : ''}</Table.Cell>
                              <Table.Cell>{r.observation_ranking || '-'}</Table.Cell>
                              <Table.Cell>{r.compliance_score ?? '-'}</Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Card.Body>
                </Card.Root>
              )}

              {pendingSections.length > 0 && (
                <Card.Root variant="outline" borderColor="yellow.300">
                  <Card.Body>
                    <HStack justify="space-between" wrap="wrap">
                      <Text fontSize="sm">
                        {t('reviewDetail.pendingCount', { count: pendingSections.length })}
                      </Text>
                      <Button size="sm" colorPalette="blue" onClick={handleReassess} loading={isReassessing}>
                        <Icon as={FiRefreshCw} mr={2} />
                        {t('reviewDetail.reassessButton')}
                      </Button>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              )}

              <Card.Root variant="outline">
                <Card.Header>
                  <HStack justify="space-between">
                    <Heading size="sm">{t('reviewDetail.evidenceHeading')}</Heading>
                    <Button size="sm" onClick={() => fileInputRef.current?.click()} loading={isUploading}>
                      <Icon as={FiUpload} mr={2} />
                      {t('reviewDetail.addEvidence')}
                    </Button>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    display="none"
                    accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      e.target.value = '';
                      if (files.length) handleFilesSelect(files);
                    }}
                  />
                  <Input
                    ref={replaceInputRef}
                    type="file"
                    display="none"
                    accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif"
                    onChange={handleReplaceFileChange}
                  />

                  {loadingEvidence ? (
                    <Spinner size="sm" />
                  ) : evidenceFiles.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">{t('reviewDetail.noEvidence')}</Text>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                      {evidenceFiles.map((evidence) => (
                        <Card.Root key={evidence.id} variant="outline" size="sm">
                          <Card.Body>
                            <VStack align="stretch" gap={2}>
                              <HStack gap={2}>
                                <Icon as={FiFile} />
                                <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{evidence.filename}</Text>
                              </HStack>

                              {evidence.verification_status && (
                                <Box
                                  p={2}
                                  bg={evidence.skipped_by_user ? 'gray.100' : evidence.verification_status === 'rejected' ? 'red.50' : 'green.50'}
                                  borderRadius="md"
                                >
                                  <Text fontSize="xs" fontWeight="semibold">
                                    {evidence.skipped_by_user
                                      ? t('createReview.evidenceSkippedTitle')
                                      : evidence.verification_status === 'rejected'
                                        ? t('createReview.evidenceRejectedTitle')
                                        : t('createReview.evidenceAcceptedTitle')}
                                  </Text>
                                  {evidence.verification_comments && (
                                    <Text fontSize="xs" color="gray.700" mt={1}>{evidence.verification_comments}</Text>
                                  )}
                                  {evidence.verification_status === 'rejected' && !evidence.skipped_by_user && (
                                    <HStack gap={2} mt={2} wrap="wrap">
                                      <Button size="xs" variant="outline" colorPalette="blue"
                                        loading={!!busyIds[evidence.id]}
                                        onClick={() => handleReplaceClick(evidence.id)}>
                                        {t('createReview.replaceFileButton')}
                                      </Button>
                                      <Button size="xs" variant="outline" colorPalette="red"
                                        loading={!!busyIds[evidence.id]}
                                        onClick={() => handleAcceptAnyway(evidence.id)}>
                                        {t('createReview.acceptAnywayButton')}
                                      </Button>
                                      <Button size="xs" variant="ghost" colorPalette="gray"
                                        loading={!!busyIds[evidence.id]}
                                        onClick={() => handleSkip(evidence.id)}>
                                        {t('createReview.skipEvidenceButton')}
                                      </Button>
                                    </HStack>
                                  )}
                                </Box>
                              )}
                            </VStack>
                          </Card.Body>
                        </Card.Root>
                      ))}
                    </SimpleGrid>
                  )}
                </Card.Body>
              </Card.Root>
            </>
          )}
        </VStack>
      </Container>
    </>
  );
};

export default ReviewDetailPage;
